import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { User, Session } from "@supabase/supabase-js";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useDateLocale } from "@/hooks/useDateLocale";
import { PageLayout } from "@/components/layout/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BookOpen, Video, Headphones, Glasses, ClipboardList, Calendar, X, RefreshCw, ChevronDown } from "lucide-react";
import { lazy, Suspense } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AssessmentContent } from "@/components/ActivityViewer/AssessmentContent";
import { AssessmentData } from "@/components/ActivityViewer/types";
import { RapidActionsGrid, CigaretteLogCard } from "@/components/actions";
import { ReliefActionsGrid, ReliefProgressStats } from "@/products/mindco-relief/components";
import { StressLogCard } from "@/products/mindco-relief/components/actions";
import { ProductId, useProduct, getProductNamespace } from "@/hooks/useProduct";

// Lazy load chart components (Recharts - 400KB)
const TrackingChart = lazy(() => import("@/components/TrackingChart").then(m => ({ default: m.TrackingChart })));
const ReliefTrackingChart = lazy(() => import("@/products/mindco-relief/components/ReliefTrackingChart").then(m => ({ default: m.ReliefTrackingChart })));
import { CrispChat } from "@/components/CrispChat";
import { CrispErrorBoundary } from "@/components/CrispErrorBoundary";
import { NextActivityCard } from "@/components/program/NextActivityCard";
import { ProgramProgressCard } from "@/components/program/ProgramProgressCard";
import { ProgramCarousel } from "@/components/program/ProgramCarousel";
import { useAnalytics } from "@/hooks/useAnalytics";
import ScheduleModal from "@/components/ScheduleModal";
import { useToast } from "@/hooks/use-toast";
import { ActivityViewer, ActivityType } from "@/components/ActivityViewer";
import { markActivityComplete } from "@/hooks/useUserProgram";
import { DashboardSkeleton } from "@/components/ui/loading-skeletons";
import { DashboardRenderer, DashboardConfig, WidgetInstance } from "@/components/dashboard/DashboardRenderer";
import { useDashboardWidgetData } from "@/hooks/useDashboardWidgetData";
import { useBrand } from "@/contexts/BrandContext";
import { supabase } from "@/integrations/supabase/client";
import {
  useBookings,
  useCancelBooking,
  dashboardKeys,
} from "@/hooks/useDashboardQueries";
import { useProgramContent } from "@/hooks/useProgramContent";
import {
  getSession,
  onAuthStateChange,
  checkIsAdminByUserId,
  hasCompletedBaselineAssessment,
  getBaselineAssessment,
} from "@/lib/api";
import { useProfile } from "@/hooks/useProfileQueries";
import { isProfileComplete } from "@/lib/utils/profileValidation";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { ConsentGuard } from "@/components/auth/ConsentGuard";

const Dashboard = () => {
  const [user, setUser] = useState<User | null>(null);
  const [_session, setSession] = useState<Session | null>(null);
  const [showBaselineAssessment, setShowBaselineAssessment] = useState(false);
  const [baselineAssessment, setBaselineAssessment] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshChart, setRefreshChart] = useState(0);
  const [programRefreshKey, setProgramRefreshKey] = useState(0);
  const [_isAdmin, setIsAdmin] = useState(false);
  const [activityViewerOpen, setActivityViewerOpen] = useState(false);
  const [activityViewerType, setActivityViewerType] = useState<ActivityType | null>(null);
  const [activityViewerId, setActivityViewerId] = useState<string | null>(null);
  const [currentPhaseActivityId, setCurrentPhaseActivityId] = useState<string | null>(null);
  const [_activeTab, setActiveTab] = useState("articles");
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [isUpcomingOpen, setIsUpcomingOpen] = useState(false);
  const [isCoachCardOpen, setIsCoachCardOpen] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // Quick action dialog states (for bottom bar Log button)
  const [cigaretteLogOpen, setCigaretteLogOpen] = useState(false);
  const [stressLogOpen, setStressLogOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackButtonClick } = useAnalytics();
  const queryClient = useQueryClient();
  const { t, _i18n } = useTranslation();
  const { productId } = useProduct();
  const { toast } = useToast();
  const dateLocale = useDateLocale();
  const productNs = getProductNamespace(productId);
  const { openProfileModal } = useProfileModal();

  // Brand context for custom dashboard
  const { brand, isPlatformBrand } = useBrand();

  // Fetch custom dashboard config for this brand/product
  const { data: customDashboardConfig, isLoading: _isDashboardConfigLoading } = useQuery({
    queryKey: ['dashboard-config', brand?.id, productId],
    queryFn: async () => {
      if (!brand?.id || isPlatformBrand) return null;
      
      // Try to find product-specific default, then brand-level default
      const { data, error } = await supabase
        .from('dashboard_configs')
        .select('*')
        .eq('brand_id', brand.id)
        .eq('is_default', true)
        .order('product_id', { ascending: false, nullsFirst: false }) // Product-specific first
        .limit(1);
      
      if (error || !data || data.length === 0) return null;
      
      const config = data[0];
      
      // Transform to DashboardConfig type
      return {
        id: config.id,
        name: config.name,
        widgets: (config.widgets || []) as unknown as WidgetInstance[],
        layout: (config.layout || { type: 'grid', columns: 12 }) as { type: 'grid' | 'masonry' | 'fixed'; columns?: number; rowHeight?: number },
        isDefault: config.is_default,
      } as DashboardConfig;
    },
    enabled: !!brand?.id && !isPlatformBrand,
    staleTime: 60000,
  });

  // Fetch widget data if using custom dashboard
  const { data: widgetData } = useDashboardWidgetData(
    customDashboardConfig ? user?.id : undefined, 
    productId
  );

  // React Query hooks for content - filtered by active program
  const { articles, videos: videoFiles, audio: audioFiles, assessments, hasActiveProgram } = useProgramContent();

  // React Query hooks for bookings
  const {
    data: upcomingBookings = [],
    isLoading: bookingLoading,
    refetch: refetchBookings,
  } = useBookings(user?.id, !!user);

  const cancelBookingMutation = useCancelBooking();

  // React Query hook for profile - uses cache, no extra API call
  const { data: profileData } = useProfile(user?.id);

  const openActivity = useCallback((type: ActivityType, id: string, phaseActivityId?: string) => {
    setActivityViewerType(type);
    setActivityViewerId(id);
    setCurrentPhaseActivityId(phaseActivityId || null);
    setActivityViewerOpen(true);
  }, []);
  
  // Helper to get product-specific translation key
  const pt = (key: string, fallback: string) => t(`${productNs}:${key}`, fallback);

  // Memoize filtered data to prevent re-filtering on every render
  const articlesList = useMemo(
    () => articles,
    [articles]
  );

  const videosList = useMemo(
    () => videoFiles,
    [videoFiles]
  );

  const audioList = useMemo(
    () => audioFiles,
    [audioFiles]
  );

  const assessmentsList = useMemo(
    () => assessments,
    [assessments]
  );

  const handleDataUpdate = useCallback(() => {
    setRefreshChart(prev => prev + 1);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  // Check profile completeness and redirect if incomplete
  useEffect(() => {
    // Only check after user is loaded and baseline assessment is complete
    if (!user || loading || showBaselineAssessment) return;

    // Wait for profile data to load
    if (!profileData) return;

    // Check if profile is complete
    if (!isProfileComplete(profileData)) {
      toast({
        title: t('common:profile.incompleteTitle', 'Complete Your Profile'),
        description: t('common:profile.incompleteDescription', 'Please complete all required fields in your profile to continue.'),
        variant: 'destructive',
      });
      openProfileModal();
    }
  }, [user, loading, showBaselineAssessment, profileData, navigate, toast, t, productId]);

  // Refetch bookings when schedule modal closes
  useEffect(() => {
    if (!scheduleModalOpen && user) {
      refetchBookings();
    }
  }, [scheduleModalOpen, user, refetchBookings]);

  // Auto-collapse coach card when user has upcoming bookings
  useEffect(() => {
    if (!bookingLoading && upcomingBookings.length > 0) {
      setIsCoachCardOpen(false);
    }
  }, [bookingLoading, upcomingBookings.length]);

  const handleCancelBooking = useCallback(async (bookingId: string) => {
    try {
      await cancelBookingMutation.mutateAsync(bookingId);
      toast({
        title: t('common:schedule.cancelled.title', 'Booking Cancelled'),
        description: t('common:schedule.cancelled.description', 'Your coaching session has been cancelled.'),
      });
      // Refetch bookings after cancellation
      refetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast({
        title: t('common:errors.error', 'Error'),
        description: t('common:schedule.cancelError', 'Failed to cancel booking. Please try again.'),
        variant: 'destructive',
      });
    }
  }, [cancelBookingMutation, refetchBookings, toast, t]);

  const handleReschedule = useCallback(async (bookingId: string) => {
    await handleCancelBooking(bookingId);
    setScheduleModalOpen(true);
  }, [handleCancelBooking]);

  const handleManualSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await refetchBookings();
      toast({
        title: t('common:schedule.synced', 'Bookings Synced'),
        description: t('common:schedule.syncedDescription', 'Your bookings have been synced with Cal.com'),
      });
    } catch (error) {
      console.error('Error syncing bookings:', error);
      toast({
        title: t('common:errors.error', 'Error'),
        description: t('common:schedule.syncError', 'Failed to sync bookings'),
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [refetchBookings, toast, t]);

  // Handle URL params for all activity types and quick actions
  useEffect(() => {
    const videoId = searchParams.get('video');
    const articleId = searchParams.get('article');
    const audioId = searchParams.get('audio');
    const assessmentId = searchParams.get('assessment');
    const phaseActivityId = searchParams.get('phaseActivityId');
    const action = searchParams.get('action');

    if (videoId) {
      openActivity('video', videoId, phaseActivityId || undefined);
      setActiveTab('videos');
      setSearchParams({});
    } else if (articleId) {
      openActivity('article', articleId, phaseActivityId || undefined);
      setActiveTab('articles');
      setSearchParams({});
    } else if (audioId) {
      openActivity('audio', audioId, phaseActivityId || undefined);
      setActiveTab('audio');
      setSearchParams({});
    } else if (assessmentId) {
      openActivity('assessment', assessmentId, phaseActivityId || undefined);
      setActiveTab('assessments');
      setSearchParams({});
    } else if (action === 'log-cigarette') {
      setCigaretteLogOpen(true);
      setSearchParams({});
    } else if (action === 'log-stress') {
      setStressLogOpen(true);
      setSearchParams({});
    }
  }, [searchParams]);

  // Handle activity completion
  const handleActivityComplete = useCallback(async () => {
    if (currentPhaseActivityId) {
      const success = await markActivityComplete(currentPhaseActivityId);
      if (success) {
        // Force all program components to refetch by changing the key
        setProgramRefreshKey(prev => prev + 1);
      }
    }
  }, [currentPhaseActivityId]);

  // Handler for quick log actions from widgets
  const handleQuickLog = useCallback(async (logType: string) => {
    if (logType === 'cigarette') {
      setCigaretteLogOpen(true);
    } else if (logType === 'stress') {
      setStressLogOpen(true);
    }
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      const result = await checkIsAdminByUserId(userId);
      if (result.success) {
        setIsAdmin(result.data);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const checkAuth = async () => {
    const sessionResult = await getSession();
    if (!sessionResult.success || !sessionResult.data) {
      navigate("/auth");
      return;
    }

    const session = sessionResult.data;
    setSession(session);
    setUser(session.user);

    const subscription = onAuthStateChange((newSession) => {
      if (!newSession) {
        navigate("/auth");
      } else {
        setSession(newSession);
        setUser(newSession.user);
      }
    });

    // Parallelize profile and admin checks for faster load (200-400ms improvement)
    await Promise.all([
      checkProfile(session.user.id),
      checkAdminStatus(session.user.id)
    ]);
    setLoading(false);

    return () => subscription.unsubscribe();
  };

  const checkProfile = async (userId: string) => {
    // Check if user has completed baseline assessment for their primary product
    const completionResult = await hasCompletedBaselineAssessment(userId, productId);

    if (completionResult.success && !completionResult.data) {
      // User hasn't completed baseline - load assessment to show
      const assessmentResult = await getBaselineAssessment(productId);

      if (assessmentResult.success && assessmentResult.data) {
        setBaselineAssessment(assessmentResult.data as AssessmentData);
        setShowBaselineAssessment(true);
      }
    }
  };

  if (!user || loading) {
    return (
      <PageLayout>
        <DashboardSkeleton />
      </PageLayout>
    );
  }

  // Separated into its own function to allow ConsentGuard to wrap it
  const renderDashboardContent = () => {


  // Render custom brand dashboard if available
  if (customDashboardConfig && customDashboardConfig.widgets.length > 0) {
    return (
      <PageLayout>
        <CrispErrorBoundary>
          <CrispChat />
        </CrispErrorBoundary>
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{pt('dashboard.welcome', 'Welcome Back!')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {pt('dashboard.continue', productId === ProductId.RELIEF ? 'Continue your wellness journey' : 'Continue your smoke-free journey')}
          </p>
        </div>
        
        <DashboardRenderer 
          config={customDashboardConfig}
          userData={widgetData ? { ...widgetData } as Record<string, unknown> : undefined}
          onQuickLog={handleQuickLog}
          className="my-6"
        />

        {/* Quick log dialogs */}
        {productId === ProductId.MINDCOTINE && (
          <div className="hidden">
            <CigaretteLogCard
              onDataUpdate={handleDataUpdate}
              externalOpen={cigaretteLogOpen}
              onExternalOpenChange={setCigaretteLogOpen}
            />
          </div>
        )}
        {productId === ProductId.RELIEF && (
          <div className="hidden">
            <StressLogCard
              onDataUpdate={handleDataUpdate}
              externalOpen={stressLogOpen}
              onExternalOpenChange={setStressLogOpen}
            />
          </div>
        )}

        {/* Baseline Assessment Modal */}
        {showBaselineAssessment && baselineAssessment && (
          <Dialog
            open={showBaselineAssessment}
            onOpenChange={() => {}}
          >
            <DialogContent
              className="max-w-2xl max-h-[90vh] overflow-y-auto"
              onInteractOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle>{baselineAssessment.title}</DialogTitle>
                {baselineAssessment.description && (
                  <DialogDescription>{baselineAssessment.description}</DialogDescription>
                )}
              </DialogHeader>
              <AssessmentContent
                assessment={baselineAssessment}
                onComplete={() => {
                  setShowBaselineAssessment(false);
                  checkProfile(user!.id);
                  queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
                }}
                onClose={() => {}}
              />
            </DialogContent>
          </Dialog>
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <CrispErrorBoundary>
        <CrispChat />
      </CrispErrorBoundary>
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{pt('dashboard.welcome', 'Welcome Back!')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {pt('dashboard.continue', productId === ProductId.RELIEF ? t('common:general.continueYourWellnessJourney', 'Continue your wellness journey') : t('common:general.continueYourSmokefreeJourney', 'Continue your smoke-free journey'))}
          </p>
        </div>

        {/* ProgressStats removed - baseline assessments are now dynamic, stats should be built based on actual assessment fields */}
        {productId === ProductId.RELIEF && <ReliefProgressStats />}

        {/* Schedule Call Card - Collapsible */}
        <Collapsible open={isCoachCardOpen} onOpenChange={setIsCoachCardOpen} className="my-6 sm:my-8">
          <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
            <CollapsibleTrigger className="w-full text-left">
              <CardHeader className="cursor-pointer hover:bg-primary/5 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-primary" />
                    {t('common:schedule.scheduleCallTitle', 'Need Support? Talk to Your Coach')}
                  </CardTitle>
                  <ChevronDown 
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                      isCoachCardOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                <CardDescription className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  {!isCoachCardOpen && upcomingBookings.length > 0 ? (
                    <span className="flex items-center gap-2 text-primary font-medium">
                      <span className="text-muted-foreground">{t('common:schedule.nextSession', 'Next session:')}</span>
                      {format(new Date(upcomingBookings[0].meeting_date), 'PPP p', { locale: dateLocale })}
                    </span>
                  ) : (
                    <span>{t('common:schedule.scheduleCallDescription', 'Book a one-on-one session with your personal coach to discuss your progress and get personalized guidance.')}</span>
                  )}
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1" />
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManualSync();
                    }}
                    disabled={isSyncing}
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                    {isSyncing ? t('common:schedule.syncing', 'Syncing...') : t('common:schedule.sync', 'Sync')}
                  </Button>
                </div>
                {bookingLoading ? (
                  <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
                ) : upcomingBookings.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-primary mb-1">
                            {t('common:schedule.upcomingMeeting', 'Next Session')}
                          </p>
                          <p className="text-sm font-semibold">
                            {format(new Date(upcomingBookings[0].meeting_date), 'PPP p', { locale: dateLocale })}
                          </p>
                          {upcomingBookings[0].meeting_url && (
                            <a 
                              href={upcomingBookings[0].meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline mt-1 inline-block"
                            >
                              {t('common:schedule.joinMeeting', 'Join Meeting')}
                            </a>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Button
                              onClick={() => handleReschedule(upcomingBookings[0].id)}
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              disabled={cancelBookingMutation.isPending}
                            >
                              <RefreshCw className="w-3 h-3 mr-2" />
                              {t('common:schedule.reschedule', 'Reschedule')}
                            </Button>
                            <Button
                              onClick={() => handleCancelBooking(upcomingBookings[0].id)}
                              variant="outline"
                              size="sm"
                              className="flex-1 text-destructive hover:text-destructive"
                              disabled={cancelBookingMutation.isPending}
                            >
                              <X className="w-3 h-3 mr-2" />
                              {t('common:schedule.cancel', 'Cancel')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {upcomingBookings.length > 1 && (
                      <Collapsible 
                        open={isUpcomingOpen} 
                        onOpenChange={setIsUpcomingOpen}
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-card border rounded-lg hover:bg-muted/50 transition-colors">
                          <p className="text-sm font-medium">
                            {t('common:schedule.allUpcoming', { count: upcomingBookings.length, defaultValue: 'All Upcoming Sessions ({{count}})' })}
                          </p>
                          <ChevronDown 
                            className={`h-4 w-4 transition-transform duration-200 ${
                              isUpcomingOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent className="mt-2">
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t('common:schedule.dateTime', 'Date & Time')}</TableHead>
                                  <TableHead>{t('common:schedule.status', 'Status')}</TableHead>
                                  <TableHead className="text-right">{t('common:schedule.actions', 'Actions')}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {upcomingBookings.map((booking) => (
                                  <TableRow key={booking.id}>
                                    <TableCell className="font-medium">
                                      {format(new Date(booking.meeting_date), 'PPP p', { locale: dateLocale })}
                                    </TableCell>
                                    <TableCell className="capitalize">{booking.status}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex justify-end gap-2">
                                        {booking.meeting_url && (
                                          <Button
                                            onClick={() => window.open(booking.meeting_url, '_blank')}
                                            variant="outline"
                                            size="sm"
                                          >
                                            {t('common:schedule.join', 'Join')}
                                          </Button>
                                        )}
                                        <Button
                                          onClick={() => handleReschedule(booking.id)}
                                          variant="outline"
                                          size="sm"
                                          disabled={cancelBookingMutation.isPending}
                                        >
                                          <RefreshCw className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          onClick={() => handleCancelBooking(booking.id)}
                                          variant="outline"
                                          size="sm"
                                          className="text-destructive hover:text-destructive"
                                          disabled={cancelBookingMutation.isPending}
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    
                    <Button 
                      onClick={() => setScheduleModalOpen(true)}
                      variant="outline"
                      className="w-full"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {t('common:schedule.scheduleAnother', 'Schedule Another Session')}
                    </Button>
                  </div>
                ) : (
                  <Button 
                    size="lg" 
                    onClick={() => {
                      trackButtonClick('Schedule Call CTA', { location: 'dashboard' });
                      setScheduleModalOpen(true);
                    }}
                    className="w-full sm:w-auto"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('common:schedule.scheduleCallButton', 'Schedule a Call')}
                  </Button>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Two-Card Layout: Next Activity + Progress */}
        <div key={`program-cards-${programRefreshKey}`} className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 sm:my-8">
          <NextActivityCard />
          <ProgramProgressCard />
        </div>

        {/* Program Carousel */}
        <ProgramCarousel 
          key={`program-carousel-${programRefreshKey}`} 
          onProgramChange={() => setProgramRefreshKey(prev => prev + 1)}
        />

        <div className="my-6 sm:my-8">
          {productId === ProductId.RELIEF ? (
            <ReliefActionsGrid onDataUpdate={handleDataUpdate} />
          ) : (
            <RapidActionsGrid onDataUpdate={handleDataUpdate} />
          )}
        </div>

        <div className="my-6 sm:my-8">
          <Suspense fallback={<div className="h-[300px] bg-muted animate-pulse rounded-lg" />}>
            {productId === ProductId.RELIEF
              ? <ReliefTrackingChart refreshKey={refreshChart} />
              : <TrackingChart key={refreshChart} />
            }
          </Suspense>
        </div>

        {hasActiveProgram && (
        <Tabs defaultValue="articles" className="space-y-4 sm:space-y-6">
          <TabsList className="w-full">
            <TabsTrigger value="articles">
              <BookOpen className="w-4 h-4" />
              <span>{t('common:dashboard.tabs.articles', 'Articles')}</span>
            </TabsTrigger>
            <TabsTrigger value="videos">
              <Video className="w-4 h-4" />
              <span>{t('common:dashboard.tabs.videos', 'Videos')}</span>
            </TabsTrigger>
            <TabsTrigger value="audio">
              <Headphones className="w-4 h-4" />
              <span>{t('common:dashboard.tabs.audio', 'Audio')}</span>
            </TabsTrigger>
            <TabsTrigger value="assessments">
              <ClipboardList className="w-4 h-4" />
              <span>{t('common:dashboard.tabs.assessments', 'Assessments')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="articles" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('common:dashboard.sections.articlesTitle', 'Educational Articles')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {articlesList.length > 0 ? (
                articlesList.map((article) => (
                  <Card 
                    key={article.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openActivity('article', article.id)}
                  >
                    <CardHeader>
                      <BookOpen className="w-8 h-8 text-primary mb-2" />
                      <CardTitle className="text-base sm:text-lg font-semibold break-words">{article.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground break-words">{article.excerpt}</CardDescription>
                    </CardHeader>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                    <CardHeader>
                      <CardDescription>{t('common:dashboard.empty.articles', 'No articles available yet. Check back soon!')}</CardDescription>
                    </CardHeader>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="videos" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('common:dashboard.sections.videosTitle', 'Video Content')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {videosList.length > 0 ? (
                videosList.map((video) => (
                  <Card 
                    key={video.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openActivity('video', video.id)}
                  >
                    <CardHeader>
                      {video.is_vr ? (
                        <Glasses className="w-8 h-8 text-primary mb-2" />
                      ) : (
                        <Video className="w-8 h-8 text-primary mb-2" />
                      )}
                      <CardTitle className="text-base sm:text-lg font-semibold break-words">{video.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground break-words">{video.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                    <CardHeader>
                      <CardDescription>{t('common:dashboard.empty.videos', 'No videos available yet. Check back soon!')}</CardDescription>
                    </CardHeader>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="audio" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('common:dashboard.sections.audioTitle', 'Audio Sessions')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {audioList.length > 0 ? (
                audioList.map((audio) => (
                  <Card 
                    key={audio.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openActivity('audio', audio.id)}
                  >
                    <CardHeader>
                      <Headphones className="w-8 h-8 text-primary mb-2" />
                      <CardTitle className="text-base sm:text-lg font-semibold break-words">{audio.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground break-words">{audio.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                    <CardHeader>
                      <CardDescription>{t('common:dashboard.empty.audio', 'No audio sessions available yet. Check back soon!')}</CardDescription>
                    </CardHeader>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="assessments" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('common:dashboard.sections.assessmentsTitle', 'Assessments')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {assessmentsList.length > 0 ? (
                assessmentsList.map((assessment) => (
                  <Card 
                    key={assessment.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openActivity('assessment', assessment.id)}
                  >
                    <CardHeader>
                      <ClipboardList className="w-8 h-8 text-primary mb-2" />
                      <CardTitle className="text-base sm:text-lg font-semibold break-words">{assessment.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground break-words">
                        {assessment.description || t('common:dashboard.assessments.description', 'Complete this assessment to track your progress')}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                    <CardHeader>
                      <CardDescription>{t('common:dashboard.empty.assessments', 'No assessments available yet. Check back soon!')}</CardDescription>
                    </CardHeader>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
        )}

        <ActivityViewer
          type={activityViewerType}
          id={activityViewerId}
          open={activityViewerOpen}
          onOpenChange={setActivityViewerOpen}
          onComplete={handleActivityComplete}
        />

      <ScheduleModal open={scheduleModalOpen} onOpenChange={setScheduleModalOpen} />

      {/* Hidden log cards triggered by bottom bar Log button */}
      {productId === ProductId.MINDCOTINE && (
        <div className="hidden">
          <CigaretteLogCard
            onDataUpdate={handleDataUpdate}
            externalOpen={cigaretteLogOpen}
            onExternalOpenChange={setCigaretteLogOpen}
          />
        </div>
      )}
      {productId === ProductId.RELIEF && (
        <div className="hidden">
          <StressLogCard
            onDataUpdate={handleDataUpdate}
            externalOpen={stressLogOpen}
            onExternalOpenChange={setStressLogOpen}
          />
        </div>
      )}

      {/* Baseline Assessment Modal - Non-dismissible onboarding */}
      {showBaselineAssessment && baselineAssessment && (
        <Dialog
          open={showBaselineAssessment}
          onOpenChange={() => {}} // Prevent closing
        >
          <DialogContent
            className="max-w-2xl max-h-[90vh] overflow-y-auto"
            onInteractOutside={(e) => e.preventDefault()} // Prevent closing by clicking outside
            onEscapeKeyDown={(e) => e.preventDefault()} // Prevent closing with Escape key
          >
            <DialogHeader>
              <DialogTitle>{baselineAssessment.title}</DialogTitle>
              {baselineAssessment.description && (
                <DialogDescription>{baselineAssessment.description}</DialogDescription>
              )}
            </DialogHeader>
            <AssessmentContent
              assessment={baselineAssessment}
              onComplete={() => {
                setShowBaselineAssessment(false);
                checkProfile(user!.id); // Recheck to ensure assessment was saved
                queryClient.invalidateQueries({ queryKey: dashboardKeys.all }); // Refresh dashboard data
              }}
              onClose={() => {}} // Required prop but modal is non-dismissible
            />
          </DialogContent>
        </Dialog>
      )}
    </PageLayout>
    );
  };

  // HIPAA Compliance: Wrap dashboard in ConsentGuard to ensure user has accepted all required consents
  return (
    <ConsentGuard userId={user.id}>
      {renderDashboardContent()}
    </ConsentGuard>
  );
};

export default Dashboard;
